import {
	type QueryObserverResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
//import type IJellyParams from "@tmna/seal-interfaces/lib/IJellyParams";
import axios from "axios";
import { useMemo } from "react";
import Dictionary from "../../packages/Dictionary";
import { QueryProvider } from "../queryClient";
import {
	type ApiClientInterface,
	EOSEndpointServiceIntent,
	type EOSServiceHistoryFormData,
	type EosServerResponseInterface,
	type GetHeadersInterface,
	type GetVehicleDealerImperativeResponse,
	type GetVehicleSpecificationsResponse,
	type IJellyParams,
	type ServiceManagementProps,
	type UpdateUserVehicleNameResponse,
	type UserVehicleData,
	type VehicleHealthProps,
	type VehicleHealthReportData,
	type VehicleInfoProps,
	type VehicleMaintenanceProps,
	type VehicleManagementProps,
	type VehicleType,
	type VehicleWithVin,
	type getJellyT,
	type memberServiceHistoryId,
} from "./index.types";

import { useConfig } from "../../_nextgen/packages/config";
import createContext from "../../_nextgen/packages/context-functions";
import Cookie from "../../packages/Cookie";
import useAnalytics from "../../packages/analytics";
import env from "../../utilities/env";
import ID_TOKEN from "../id_token";
import { getReCAPTCHAToken } from "./getReCAPTCHAToken";

export const endpoint = env("REACT_APP_EOS_ENDPOINT");

const REFERRER_URL = env("REACT_APP_REFERRER_URL") || window.location.hostname;
const endpoint_oauth = env("REACT_APP_EOS_OAUTH_API_ENDPOINT");

const getEOSBaseUrl = (intent: EOSEndpointServiceIntent, action: string) => {
	return `${env(`REACT_APP_EOS_V2_${intent}_ENDPOINT`)}${action}`;
};

const buildEOSEndpoint = (url, params = {}) => {
	const { intent } =
		services.find((service) => {
			const actionUrl = Object.values(actions).find((action) => {
				return url === action;
			});
			const actionKey = Object.keys(actions).find(
				(key) => actions[key] === actionUrl,
			) as string;
			return service.actions.includes(actionKey);
		}) || {};

	return !intent
		? endpoint + Dictionary(url, params)
		: getEOSBaseUrl(intent, Dictionary(url, params));
};

const apiClient = ({
	oauth,
	captcha = false,
	headers = {},
	opts = {},
	params = {},
}: ApiClientInterface) => {
	const axiosInstance = axios.create({
		headers: getHeaders({ headers, mode: process.env.NODE_ENV }),
		...opts,
	});
	axiosInstance.interceptors.request.use(async (config) => {
		config.url = buildEOSEndpoint(config.url as string, params);

		if (oauth) {
			config.headers.Authorization = await getOAuthToken(oauth);
		}

		if (captcha) {
			if (config.method !== "get") {
				config.data = {
					...config.data,
					tokenAttributes: {
						xauthToken: await getReCAPTCHAToken(),
						xconsumer: env("REACT_APP_RECAPTCHA_CONSUMER") || "LD",
						xreferrer: REFERRER_URL,
					},
				};
			} else {
				config.headers["x-auth-token"] = await getReCAPTCHAToken();
				config.headers["x-consumer"] =
					env("REACT_APP_RECAPTCHA_CONSUMER") || "LD";
				config.headers["x-referrer"] = REFERRER_URL;
			}
		}
		return config;
	});
	return axiosInstance;
};

export const EOS = {
	serviceManagement: ({
		vin,
		oauth,
		captcha,
		memberServiceHistoryId,
	}: ServiceManagementProps) => {
		return apiClient({
			oauth,
			captcha,
			params: { vin, memberServiceHistoryId },
		});
	},
	vehicleMaintenance: ({ oauth, captcha = true }: VehicleMaintenanceProps) => {
		return apiClient({ captcha, oauth });
	},
	vehicleManagement: ({
		vehicleId,
		vin,
		model,
		year,
	}: VehicleManagementProps) => {
		return apiClient({
			params: { vehicleId, vin, model, year },
			captcha: true,
		});
	},
	vehicleHealth: ({ vin }: VehicleHealthProps) => {
		return apiClient({ params: { vin } });
	},
	vehicleInfo: ({ oauth }: VehicleInfoProps) => {
		return apiClient({ oauth, captcha: true });
	},
};

const services = [
	{
		intent: EOSEndpointServiceIntent.SERVICE_MANAGEMENT,
		actions: [
			"getOptCodes",
			"getSafetyCampaigns",
			"getMasterPreferences",
			"getAllVehicleSubscriptions",
			"addVehicleServiceHistory",
			"getVehicleServiceHistoryDealer",
			"getVehicleServiceHistory",
			"updateVehicleServiceHistory",
			"deleteVehicleServiceHistory",
		],
	},
	{
		intent: EOSEndpointServiceIntent.VEHICLE_MAINTENANCE,
		actions: [
			"getModelAndYears",
			"getVehicleManuals",
			"maintenanceSchedule",
			"getVinMaintenanceSchedule",
			"getMaintenanceSchedule",
			"getEngineTransDrive",
		],
	},
	{
		intent: EOSEndpointServiceIntent.VEHICLE_MANAGEMENT,
		actions: [
			"getUserVehicles",
			"setPreferredDealer",
			"addCustomerVehicles",
			"removeCustomerVehicle",
			"renameVehicle",
			"setPreferredVehicle",
			"getVehicleDealer",
		],
	},
	{
		intent: EOSEndpointServiceIntent.VEHICLE_HEALTH,
		actions: ["getVhr", "getVehicleHealthHistory"],
	},
	{
		intent: EOSEndpointServiceIntent.VEHICLE_INFO,
		actions: ["getModelAndYears", "getSafetyCampaigns", "getVehicleSpecs"],
	},
];

export const actions = {
	getModelAndYears: "/v1/vehicle/model-year-list?format=model-year",
	getVehicleManuals: "/v2/vehicle/manual",
	getOptCodes: "/v1/vehicle/service-op-codes",
	getSafetyCampaigns: "/v2/vehicle/safety-campaigns",
	getMasterPreferences: "/v1/customer/communication/master-preferences",
	customerPreferences: "/v1/customer/communication/preferences",
	getAllVehicleSubscriptions: "/v1/customer/subscriptions",
	getUserVehicles: "/v1/customer/vehicles",
	setPreferredDealer: "/v1/customer/preferred-dealer",
	addCustomerVehicles: "/v1/customer/vehicles",
	maintenanceSchedule:
		"/v1/vehicle/maintenance-schedule?vin={vin}&lastKnownMileage=",
	getVhr: "/v1/vehicle/{vin}/vehicle-health-report",
	getVehicleHealthHistory: "/v1/vehicle/{vin}/vehicle-health-history",
	addVehicleServiceHistory: "/v1/vehicle/{vin}/self",
	getVehicleServiceHistoryDealer: "/v1/vehicle/{vin}/self/?serviceType=NSH",
	getVehicleServiceHistory: "/v1/vehicle/{vin}/self/?serviceType=THIRD",
	updateVehicleServiceHistory:
		"/v1/vehicle/{vin}/service-records/{memberServiceHistoryId}",
	deleteVehicleServiceHistory:
		"/v1/vehicle/{vin}/service-records/{memberServiceHistoryId}",
	removeCustomerVehicle:
		"/v1/customer/vehicles/{vin}?reason=disposed&model={model}&modelYear={year}",
	renameVehicle: "/v1/customer/vehicles/{vehicleId}",
	setPreferredVehicle: "/v1/customer/vehicles/{vehicleId}",
	getVinMaintenanceSchedule: "/v2/vehicle/maintenance-schedule",
	getMaintenanceSchedule: "/v2/vehicle/maintenance-scheduled",
	getEngineTransDrive: "/v2/vehicle/engine-transmission-drive",
	getVehicleSpecs: "/v2/vehicle/specification",
	getPDF: "/v1/vehicle/{vin}/vehicle-health-report/{vhrId}/pdf",
	getVehicleDealer: "/v1/vehicle/{vin}/dealer",
} as const;

const oauth = {
	getModelAndYears: "modelYear",
	getSafetyCampaigns: "safetyCampaign",
	getVehicleManuals: "manuals",
	getMasterPreferences: "masterPreferences",
	getOptCodes: "optCodes",
	getMaintenanceSchedule: "maintenanceScheduleDmy",
	getMaintenanceScheduleVIN: "maintenanceschedulevin",
	getEngineTransDrive: "maintenanceScheduleEtd",
	getVehicleSpecs: "vehicleSpecs",
};

export function pad(input, size) {
	let num = input?.toString() || "";
	while (num.length < size) num = `0${num}`;
	return num;
}

function getModelFromTrim(trim = "") {
	return trim?.replace(/([0-9\s]+)/g, "");
}

const generateVehicleImageEndpoint = (
	vehicle: IJellyParams & { vehicleId: string; exteriorColorCode?: string },
) => {
	if (!vehicle || typeof vehicle !== "object") return undefined;
	const { vehicleId, year, model, exteriorColorCode } = vehicle;
	const query = `vehicleId:${vehicleId};year:${year};model:${getModelFromTrim(
		model,
	)};trim:${model}${
		exteriorColorCode ? `;code:${pad(exteriorColorCode, 4)}` : ""
	}`;
	if (!query) return undefined;
	return `/rest/lexusdrivers/vehicledata?type=all&multipleVehicles=${query}`;
};

const generateVehicleImagesEndpoint = (vehicles: VehicleType[] = []) => {
	if (!Array.isArray(vehicles) || vehicles.length === 0) return undefined;
	const query = vehicles
		.map(
			(v) =>
				`vehicleId:${v?.vehicleId};year:${v?.year};model:${getModelFromTrim(
					v?.model,
				)};trim:${v?.model}${
					v?.exteriorColorCode ? `;code:${pad(v?.exteriorColorCode, 4)}` : ""
				}`,
		)
		.join(",");
	if (!query) return undefined;
	return `/rest/lexusdrivers/vehicledata?type=all&multipleVehicles=${query}`;
};

export const EOSProvider = QueryProvider;

export const withEOSProvider = (Component) => (props) => (
	<EOSProvider>
		<Component {...props} />
	</EOSProvider>
);

const getHeaders = ({ headers, mode = "production" }: GetHeadersInterface) => {
	const [id_token] = Cookie("id_token");
	const [id_token_local] = Cookie("id_token_local");
	const [id_token_tmnab2cqa] = Cookie("id_token_tmnab2cqa");
	const auth =
		(mode !== "production" ? id_token_local : undefined) ||
		id_token ||
		id_token_tmnab2cqa ||
		ID_TOKEN ||
		"";

	const retHeaders = {
		"x-brand": env("REACT_APP_EOS_BRAND") || "L",
		Authorization: auth, // id_token or oauth token
		...headers,
	};

	// Delete undefined keys
	Object.keys(retHeaders).forEach((key) =>
		retHeaders[key] === undefined ? delete retHeaders[key] : {},
	);

	return retHeaders;
};

const truncateMessage = (message, minChars, maxChars) => {
	return (
		message &&
		(message.length > maxChars ? message.slice(minChars, maxChars) : message)
	);
};

const oauthClient = (opts = {}) => {
	return axios.create({
		...opts,
	});
};

function getOAuthToken(path) {
	return oauthClient()
		.post(endpoint_oauth, { resource: path })
		.then((data) => data?.data?.body?.data?.AccessToken);
}
const getQueryKey = (queryNames?: (string | undefined | null)[]) => {
	if (Array.isArray(queryNames)) {
		const queryNamesWithoutNull = queryNames.filter(
			(queryName) => typeof queryName === "string",
		);
		return ["eos"].concat(queryNamesWithoutNull);
	}
	return [];
};

const getvehicleMaintenance = () => {
	return EOS.vehicleMaintenance({
		oauth: oauth.getModelAndYears,
		captcha: false,
	}).get(actions.getModelAndYears);
};

export function useGetModelAndYears() {
	return useQuery(getQueryKey(["modelYearList"]), getvehicleMaintenance, {
		select: (responseData) =>
			responseData?.data?.data?.vehicleModelYearList || [],
	});
}

export function useGetVehicleManuals({ model, year, vin }) {
	const queryKey = model && year && !vin ? { model, year } : { vin };
	return useQuery(
		getQueryKey(["vehicle-manuals", ...Object.values(queryKey)]),
		() =>
			EOS.vehicleMaintenance({ oauth: oauth.getVehicleManuals }).post(
				actions.getVehicleManuals,
				{
					keyword: "all",
					...queryKey,
				},
			),
		{
			enabled: !!((model && year) || !!vin?.length),
			select: (data) => data?.data?.data,
		},
	);
}

export function useGetVehicleGuides({ model, year }) {
	const hasModelAndYear = model && year;
	return useQuery(
		getQueryKey(["vehicle-guides", model, year]),
		() =>
			axios.get(
				`/rest/lexusdrivers/vehicledocs?year=${year}&model=${encodeURIComponent(
					model,
				)}`,
			),
		{
			enabled: !!hasModelAndYear,
			select: (responseData) => {
				return responseData?.data
					? { ...responseData.data }
					: { data: responseData };
			},
		},
	);
}

const getSafetyCampaigns = (vin: string) => {
	return EOS.vehicleInfo({ oauth: oauth.getSafetyCampaigns }).post(
		actions.getSafetyCampaigns,
		{
			vin,
		},
	);
};
export function useGetSafetyCampaigns(vin) {
	return useQuery(
		getQueryKey(["safety-campaigns", vin]),
		() => getSafetyCampaigns(vin),
		{
			enabled: !!vin,
			select: (responseData) => {
				return responseData?.data?.body?.data;
			},
		},
	);
}

const getMasterPreferences = () => {
	return apiClient({ oauth: oauth.getMasterPreferences }).get(
		actions.getMasterPreferences,
	);
};

export function useGetMasterPreferences() {
	return useQuery(getQueryKey(["master-preferences"]), getMasterPreferences, {
		select: (responseData) => {
			return responseData?.data
				? { ...responseData.data }
				: { data: responseData };
		},
	});
}

const getOptCodes = () => {
	return EOS.serviceManagement({ oauth: oauth.getOptCodes }).get(
		actions.getOptCodes,
	);
};

export function useGetOptCodes() {
	const { data, isLoading } = useQuery(getQueryKey(["opt-codes"]), getOptCodes);
	return {
		serviceOptions: data?.data?.data?.opCodes || [],
		isLoading,
	};
}

const getVehicleHealthReport = (vin?: string | null | undefined) => {
	return apiClient({ params: { vin } }).get(actions.getVhr);
};

export function useGetVehicleHealthReport(
	vin: string | null | undefined,
	shouldFetchData = true,
): QueryObserverResult<VehicleHealthReportData> {
	return useQuery(
		getQueryKey(["vehicle-health-report", vin]),
		() => getVehicleHealthReport(vin),
		{
			enabled: !!vin && shouldFetchData,
			select: (responseData) => {
				return responseData?.data?.data || {};
			},
		},
	);
}

const getVehicleDealer = (vin?: string | null | undefined) => {
	return apiClient({ captcha: false, params: { vin } }).get(
		actions.getVehicleDealer,
	);
};
export function useGetVehicleDealer(
	vin: string | undefined | null,
	enabled: boolean,
) {
	return useQuery(
		getQueryKey(["vehicle-dealer", vin]),
		() => getVehicleDealer(vin),
		{
			enabled: !!vin && enabled,
			select: (responseData) => {
				const response = responseData?.data
					? { ...responseData.data }
					: { data: responseData };
				return response?.body?.data || {};
			},
		},
	);
}

export function useVehicleDealerService() {
	return async (vin: string) => {
		const data = await apiClient({ captcha: false, params: { vin } }).get(
			actions.getVehicleDealer,
		);
		const response = data?.data ? { ...data?.data } : { data };

		return (response?.body?.data ?? {}) as GetVehicleDealerImperativeResponse;
	};
}

const getCustomerPreferences = () => {
	return apiClient({}).get(actions.customerPreferences);
};
export function useCustomerPreferences() {
	return useQuery(
		getQueryKey(["customer-preferences"]),
		getCustomerPreferences,
		{
			select: (responseData) => {
				return responseData?.data
					? { ...responseData.data }
					: { data: responseData };
			},
		},
	);
}

const getAllVehicleSubscriptions = () => {
	return apiClient({ captcha: true }).get(actions.getAllVehicleSubscriptions);
};

export function useGetAllVehicleSubscriptions(isLoggedIn) {
	return useQuery(
		getQueryKey(["vehicle-subscriptions", isLoggedIn]),
		getAllVehicleSubscriptions,
		{
			enabled: !!isLoggedIn,
			select: (responseData) => {
				return responseData?.data?.data?.vehicleSubscription;
			},
		},
	);
}

const getVehicles = () => {
	return apiClient({ captcha: true }).get(actions.getUserVehicles);
};

const getImages = (imagesEndpoint: string) => {
	return axios.get(imagesEndpoint);
};

function useApiCall(apiCall = () => {}, deps: unknown[] = [], options = {}) {
	const { data, isInitialLoading, status }: { [key: string]: any } = useQuery(
		deps,
		apiCall,
		options,
	);
	const response = data?.data
		? { ...data?.data, isInitialLoading, status }
		: { data: data, isInitialLoading, status };
	return response;
}

export function useGetUserVehicles(isLoggedIn?: boolean): UserVehicleData {
	const {
		data: vehicles = [],
		isInitialLoading,
		status,
	} = useApiCall(
		() => apiClient({ captcha: true }).get(actions.getUserVehicles),
		["user-vehicles"],
		{
			enabled: !!isLoggedIn,
		},
	);
	const imagesEndpoint = generateVehicleImagesEndpoint(vehicles);
	const shouldFetchImages =
		isLoggedIn && Array.isArray(vehicles) && imagesEndpoint;
	const { data: images = {} } = useApiCall(
		() => imagesEndpoint && axios.get(imagesEndpoint),
		["user-vehicles-images", imagesEndpoint],
		{ enabled: !!shouldFetchImages },
	);
	if (!Array.isArray(vehicles)) {
		return {
			status,
			vehicles: [],
			isInitialLoadingUserVehicles: isInitialLoading,
		};
	}
	const mergedData = vehicles.map((v) => ({
		...v,
		...(images?.[v.vehicleId] || []),
	}));
	const data = {
		status,
		vehicles: mergedData,
		isInitialLoadingUserVehicles: isInitialLoading,
	};

	return data;
}

export const JellyContext = createContext();

const fetchVehicleImageData: getJellyT = async (vehicle) => {
	try {
		const imageEndpoint = generateVehicleImageEndpoint(vehicle);
		if (!imageEndpoint) throw new Error("Image endpoint is undefined");
		const response = await axios.get(imageEndpoint);

		if (response.status === 200) {
			const data = response.data.data;
			const vehicleId = Object.keys(data)[0];
			const { banner } = data[vehicleId];

			return {
				imageSrc: banner?.desktopImage,
			};
		}

		return {
			imageSrc: null,
		};
	} catch (error) {
		return {
			imageSrc: null,
		};
	}
};

const getJelly = JellyContext.inject(fetchVehicleImageData);

export function useGetPrimaryVehicle(
	vehicle,
): QueryObserverResult<VehicleWithVin> {
	const { SEAL } = useConfig();
	const getJelly = SEAL?.getJellyImage || fetchVehicleImageData;
	return useQuery(
		getQueryKey(["user-jelly-vehicle-image", vehicle?.vehicleId]),
		() => getJelly(vehicle),
		{
			enabled: !!vehicle,
			cacheTime: 0,
			staleTime: 0,
			select: (responseData) => {
				return {
					...vehicle,
					jellyImageUrl: responseData,
				};
			},
		},
	);
}

const getServiceManagementHistory = (vin: string) => {
	return EOS.serviceManagement({ vin }).get(actions.getVehicleServiceHistory);
};

const getVehicleServiceHistoryDealer = (vin: string) => {
	return EOS.serviceManagement({ vin }).get(
		actions.getVehicleServiceHistoryDealer,
	);
};

export function useGetVehicleServiceHistory(vin) {
	const { data, isLoading } = useQuery(
		getQueryKey(["vehicle-service-history", vin]),
		() => getServiceManagementHistory(vin),
		{ enabled: !!vin },
	);
	const { data: dealerData } = useQuery(
		getQueryKey(["vehicle-service-history-dealer", vin]),
		() => getVehicleServiceHistoryDealer(vin),
		{ enabled: !!vin },
	);
	const { serviceHistoryRecords = [] } = data?.data?.data || {};
	const { repairOrders = [] } = dealerData?.data?.data || {};
	const combinedData = useMemo(
		() => ({ serviceHistoryRecords, repairOrders }),
		[serviceHistoryRecords, repairOrders],
	);
	return { data: combinedData, isLoading };
}

export function useAddVehicleServiceHistory(vin) {
	const fireAnalytics = useAnalytics("AddServiceHistory");
	const client = useQueryClient();
	const data = useMutation({
		mutationFn: (args: EOSServiceHistoryFormData) => {
			return EOS.serviceManagement({ vin })
				.post(actions.addVehicleServiceHistory, {
					...args,
				})
				.catch((error) => {
					const truncatedError = truncateMessage(
						error?.response?.data?.error?.detail,
						0,
						100,
					);
					fireAnalytics("error", {
						code: error?.response?.data?.error?.code,
						errorMessage: truncatedError,
					});
					return Promise.reject(error?.response?.data?.error);
				})
				.then((data: EosServerResponseInterface) => {
					if (data?.error) {
						fireAnalytics("error", {
							code: data?.error?.code,
							errorMessage: truncateMessage(data?.error?.detail, 0, 100),
						});
						return Promise.reject(data?.error);
					}
					return data;
				});
		},
		onSettled: async () => {
			await client.invalidateQueries(["vehicle-service-history"]);
			await client.invalidateQueries(["vehicle-service-history-dealers"]);
		},
	});

	return data;
}

export function useUpdateVehicleServiceHistory(vin) {
	const fireAnalytics = useAnalytics("UpdateServiceHistory");
	const client = useQueryClient();
	return useMutation({
		mutationFn: ({
			memberServiceHistoryId,
			...args
		}: EOSServiceHistoryFormData & memberServiceHistoryId) => {
			return EOS.serviceManagement({ vin, memberServiceHistoryId })
				.patch(actions.updateVehicleServiceHistory, {
					...args,
				})
				.catch((error) => {
					const truncatedError = truncateMessage(
						error?.response?.data?.error?.detail,
						0,
						100,
					);
					fireAnalytics("error", {
						code: error?.response?.data?.error?.code,
						errorMessage: truncatedError,
					});
					return Promise.reject(error?.response?.data?.error);
				})
				.then((data: EosServerResponseInterface) => {
					if (data?.error) {
						fireAnalytics("error", {
							code: data?.error?.code,
							errorMessage: truncateMessage(data?.error?.detail, 0, 100),
						});
						return Promise.reject(data?.error);
					}
					return data;
				});
		},
		onSettled: async () => {
			await client.invalidateQueries(["vehicle-service-history"]);
			await client.invalidateQueries(["vehicle-service-history-dealers"]);
		},
	});
}

export function useDeleteVehicleServiceHistory(vin) {
	const fireAnalytics = useAnalytics("DeleteServiceHistory");
	const client = useQueryClient();
	return useMutation({
		mutationFn: (memberServiceHistoryId: string) => {
			return EOS.serviceManagement({ vin, memberServiceHistoryId })
				.delete(actions.deleteVehicleServiceHistory)
				.catch((error) => {
					fireAnalytics("error", {
						code: error?.response?.data?.error?.code,
						errorMessage: truncateMessage(
							error?.response?.data?.error?.detail,
							0,
							100,
						),
					});
					return Promise.reject(error?.response?.data?.error);
				})
				.then((data: EosServerResponseInterface) => {
					if (data.error) {
						fireAnalytics("error", {
							code: data?.error?.code,
							errorMessage: truncateMessage(data?.error?.detail, 0, 100),
						});
						return Promise.reject(data?.error);
					}
					return data;
				});
		},
		onSettled: async () => {
			await client.invalidateQueries(["vehicle-service-history"]);
			await client.invalidateQueries(["vehicle-service-history-dealers"]);
		},
	});
}

export function useAddCustomerVehicles() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async ({
			modelYearList,
			vinList,
		}: { modelYearList?: any[]; vinList?: string[] }) =>
			await apiClient({ captcha: true }).post(actions.addCustomerVehicles, {
				modelYearList,
				VinList: vinList?.map((i) => ({ vin: i })),
			}),
		onSettled: async (_data, error) => {
			if (!error) {
				return client.invalidateQueries(["user-vehicles"]);
			}
		},
		onMutate: async () => {
			await client.cancelQueries({ queryKey: ["user-vehicles"] });
			// Snapshot the previous value
			const previousVehicles = client.getQueryData(["user-vehicles"]);
			return { previousVehicles };
		},
		onError: async (_error, _variables, context) => {
			await client.invalidateQueries(["user-vehicles"]);
			if (context?.previousVehicles) {
				client.setQueryData(["user-vehicles"], context.previousVehicles);
			}
		},
	});
}

export function useRemoveCustomerVehicle() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async ({
			vin,
			model,
			year,
		}: { vin: string; model: string; year: string }) => {
			return await EOS.vehicleManagement({ vin, model, year }).delete(
				actions.removeCustomerVehicle,
			);
		},
		onSuccess: async () => {
			return client.invalidateQueries({ queryKey: ["user-vehicles"] });
		},
	});
}

export function useSetPreferredVehicle() {
	const client = useQueryClient();

	return useMutation({
		mutationFn: async ({ vehicleId, model, year }: VehicleType) => {
			return await EOS.vehicleManagement({ vehicleId }).patch(
				actions.setPreferredVehicle,
				{
					preferredVehicle: "Y",
					model: vehicleId && vehicleId.length > 17 ? model : undefined,
					year: vehicleId && vehicleId.length > 17 ? year : undefined,
				},
			);
		},
		onMutate: async ({ vehicleId }) => {
			await client.cancelQueries({ queryKey: ["user-vehicles"] });

			// Snapshot the previous value
			const previousVehicles = client.getQueryData(["user-vehicles"]);

			// Optimistically update to the new value
			client.setQueryData(
				["user-vehicles"],
				(vehicles: { [key: string]: any }) => {
					return vehicles?.data?.data?.reduce(
						(acc, vehicle) => [
							...acc,
							vehicle.vehicleId === vehicleId || vehicle.vin === vehicleId
								? { ...vehicle, preferredVehicle: "Y" }
								: vehicle,
						],
						[],
					);
				},
			);
			// Return a context object with the snapshotted value
			return { previousVehicles };
		},
		onError: ({ context }) => {
			client.setQueryData(["user-vehicles"], context.previousVehicles);
		},
		onSuccess: async () => {
			// invalidate onSuccess to force fetch of latest data
			return client.invalidateQueries({ queryKey: ["user-vehicles"] });
		},
	});
}

export function useSetPreferredDealer() {
	const client = useQueryClient();

	return useMutation({
		mutationFn: ({ vin, dealerCode }: { vin: string; dealerCode: string }) =>
			EOS.vehicleManagement({}).post(actions.setPreferredDealer, {
				vinList: [{ vin }],
				dealerCode,
			}),
		onSuccess: async () => {
			return client.invalidateQueries({ queryKey: ["user-vehicles"] });
		},
	});
}

export function useRenameVehicle() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: ({
			vehicleId,
			model,
			year,
			nickname,
		}: { vehicleId: string; model: string; year: string; nickname: string }) =>
			EOS.vehicleManagement({ vehicleId }).patch<UpdateUserVehicleNameResponse>(
				actions.renameVehicle,
				{
					model: vehicleId?.length > 17 ? model : undefined,
					nickName: nickname,
					reason: "",
					reportedBy: "OWNER",
					year: vehicleId?.length > 17 ? year : undefined,
				},
			),
		onSettled: async () => {
			return client.invalidateQueries({ queryKey: ["user-vehicles"] });
		},
	});
}

const getEngineTransDrive = (
	division: string,
	engine: string,
	transmission: string,
	driveType: string,
	model: string,
	year: string,
	lastKnownMileage: string,
) => {
	return EOS.vehicleMaintenance({ oauth: oauth.getEngineTransDrive }).post(
		actions.getEngineTransDrive,
		{
			division: division || "LEXUS",
			engine,
			transmission,
			driveType,
			model,
			year,
			lastKnownMileage,
		},
	);
};

export function useGetEngineTransDrive(
	{ engine, transmission, driveType, model, year, lastKnownMileage },
	enabled = true,
) {
	const {
		data = {},
		isInitialLoading,
		...rest
	} = useQuery(
		["etd", { driveType, model, year, lastKnownMileage }],
		() =>
			getEngineTransDrive(
				"LEXUS",
				engine,
				transmission,
				driveType,
				model,
				year,
				lastKnownMileage,
			),
		{
			enabled:
				!!(engine && transmission && driveType && model && year) && enabled,
			select: (data) => data?.data?.data,
		},
	);
	return {
		...data.scheduleMaintenance,
		isInitialLoading,
		...rest,
	};
}

const getVinMaintenanceSchedule = (vin: string, lastMileage: string) => {
	return EOS.vehicleMaintenance({
		oauth: oauth.getMaintenanceScheduleVIN,
	}).post(actions.getVinMaintenanceSchedule, {
		vin,
		operCondid: "",
		specialInstructionId: "",
		lastKnownMileage: lastMileage,
	});
};

const getMaintenanceSchedule = (model: string, year: string) => {
	return EOS.vehicleMaintenance({
		oauth: oauth.getMaintenanceSchedule,
		captcha: false,
	}).post(actions.getMaintenanceSchedule, {
		model,
		year,
		division: "LEXUS",
	});
};

export function useGetMaintenanceSchedule(
	vin,
	pModel,
	year,
	driveType,
	lastKnownMileage,
) {
	const model = pModel?.endsWith("L") ? pModel.replace("L", "") : pModel;
	const hasModelAndYear = model && year;
	const lastMileage =
		!lastKnownMileage || lastKnownMileage === "0" ? "5000" : lastKnownMileage;

	const vinQuery = useQuery(
		["maintenance-schedule", vin, lastMileage],
		() => getVinMaintenanceSchedule(vin, lastMileage),
		{ enabled: !!vin },
	);

	const modelYearQuery = useQuery(
		["maintenance-schedule", model, year],
		() => getMaintenanceSchedule(model, year),
		{
			enabled: !!(hasModelAndYear && !vin),
		},
	);

	const vinData = vinQuery.data?.data?.data;
	const modelYearData = modelYearQuery.data?.data?.data || {};

	const maintenanceScheduledEngineDriveType =
		modelYearData?.scheduleMaintenance?.engineTransDrive[0] || {};

	const engineTransDrive = {
		...driveType,
		...maintenanceScheduledEngineDriveType,
	};

	const mileage =
		lastKnownMileage ||
		(modelYearData?.scheduleMaintenance?.lastKnownMileage === "0" ||
		!lastKnownMileage
			? "5000"
			: modelYearData?.scheduleMaintenance?.lastKnownMileage);

	const hasApiData = Object.keys(modelYearData).length > 0;
	const driveData = useGetEngineTransDrive(
		{
			...engineTransDrive,
			model,
			year,
			lastKnownMileage: mileage,
		},
		!vin && hasApiData,
	);

	const maintenanceSchedule = vinData?.scheduleMaintenance || driveData;

	return {
		etdList: maintenanceSchedule?.engineTransDrive,
		...maintenanceSchedule,
		isInitialLoading:
			vinQuery?.isInitialLoading ||
			modelYearQuery?.isInitialLoading ||
			driveData?.isInitialLoading,
	};
}

const getVehicleSpecifications = (vin: string) => {
	return EOS.vehicleInfo({
		oauth: oauth.getVehicleSpecs,
	}).post<GetVehicleSpecificationsResponse>(actions.getVehicleSpecs, {
		vin,
	});
};

export function useVehicleSpecifications(vin) {
	return useQuery(["vehicle-specs", vin], () => getVehicleSpecifications(vin), {
		enabled: !!vin,
		select: (responseData) => {
			return responseData?.data?.data;
		},
	});
}

const getVehicleHealth = (vin: string) => {
	return EOS.vehicleHealth({ vin }).get(actions.getVehicleHealthHistory);
};

export function useGetVehicleHealthHistory(vin) {
	return useQuery(
		["vehicle-health-history", vin],
		() => getVehicleHealth(vin),
		{
			enabled: !!vin,
			select: (responseData) => {
				return responseData?.data?.data?.vhrData;
			},
		},
	);
}

export function getPDF(vin, vhrId) {
	if (vin && vhrId) {
		apiClient({
			headers: { accept: "application/pdf" },
			opts: { responseType: "blob" },
			params: { vin, vhrId },
		})
			.get(actions.getPDF)
			.then((res) => {
				const link = document.createElement("a");
				link.href = URL.createObjectURL(new Blob([res.data]));
				link.download = `vehicle-health-report.${vhrId}.pdf`;
				document.body.append(link);
				link.click();
				return res?.data?.data;
			})
			.catch((err) => err);
	}
}
